<template>
  <el-container>
    <FaqPanel
      page="campaigns_new"
      :top-offset="50"
      :max-width="350"
      :backdrop="true"
    />
    <el-main>
      <h3 class="heading">New campaign</h3>
      <el-card>
        <MediakitInfo v-if="mediaKit" :media-kit="mediaKit" />
        <CampaignForm
          v-if="!loadingCampaign"
          v-model="campaignItem"
          :loading="loading"
          action-text="Create"
          :media-kit="mediaKit"
          @submit="onSubmit"
          @update:modelValue="update"
        />
      </el-card>
      <LoadingBlock :loading="loadingCampaign" />
    </el-main>
  </el-container>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import {
  AdType,
  CampaignValidationErrorsKey,
  RonType,
  Stream,
  IspMode
} from '@/api/types'
import { ICampaign, ICampaignAttributes } from '@/api/campaigns'
import { IMediaKitAttributes } from '@/api/media_kits'
import useRouter from '@/hooks/useRouter'
import { useCampaignsStore } from '@/apps/traforama/store/campaigns'
import CampaignForm from '../components/CampaignForm.vue'
import useValidation from '@/hooks/useValidation'
import LoadingBlock from '@/components/LoadingBlock.vue'
import MediakitInfo from '../components/MediakitInfo.vue'

import { useIPListsStore } from '@/apps/traforama/store/ipLists'
import { useRetargetingListsStore } from '@/apps/traforama/store/retargetingLists'
import { useSpotsListsStore } from '@/apps/traforama/store/spotsLists'
import { useMediaKitsStore } from '@/apps/traforama/store/mediaKits'
import { safeSubmitProcessing, removeLocalStorageKey } from '@/utils/helpers'
import { useCreativesStore } from '@/apps/traforama/store/creatives'
import FaqPanel from '@/components/FaqPanel/Index.vue'
import { event } from 'vue-gtag'
import { useOnboardingRedirect } from '@/hooks/useOnboardingRedirect'

const DEFAULT_RPS_FACTOR = 480
const DEFAULT_SLOW_STOP_FACTOR = 25

export default defineComponent({
  name: 'NewCampaign',
  components: {
    CampaignForm,
    LoadingBlock,
    MediakitInfo,
    FaqPanel
  },
  props: {
    id: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: AdType.Popunder
    },
    stream: {
      type: String,
      default: Stream.Adult
    },
    domain: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: ''
    },
    mediaKitId: {
      type: String,
      default: ''
    }
  },
  setup(props) {
    const mediaKitsStore = useMediaKitsStore()
    const campaignsStore = useCampaignsStore()
    const creativesStore = useCreativesStore()
    const spotsListsStore = useSpotsListsStore()
    const ipListsStore = useIPListsStore()
    const retargetingListsStore = useRetargetingListsStore()

    removeLocalStorageKey('media_kit_id')

    ipListsStore.FetchItems({})
    retargetingListsStore.FetchItems({})

    const loadingCampaign = ref(false)
    const mediaKit = ref<IMediaKitAttributes | undefined>(undefined)
    let campaignItem = ref<ICampaignAttributes | undefined>({
      id: undefined,
      name: '',
      disabled: false,
      stream: Number(props.stream),
      type_id: Number(props.type),
      ron_type: RonType.All,
      spot_ids: [],
      country_codes: [],
      city_ids: [],
      region_ids: [],
      isp: '',
      ips: [],
      device_ids: [],
      connection_type_ids: [],
      platform_ids: [],
      specific_platforms: [],
      browser_ids: [],
      specific_browsers: [],
      spots_list_ids: [],
      ip_list_ids: [],
      start_at: '',
      finish_at: '',
      limits: [],
      weekdays: [],
      hours: [],
      size_id: Number(props.size),
      whitelist_domains: props.domain.length ? [props.domain] : [],
      blacklist_domains: [],
      use_whitelist_domains: props.domain.length > 0,
      whitelist_user_agents: [],
      blacklist_user_agents: [],
      use_whitelist_user_agents: false,
      available_traffic: 0,
      max_traffic: 0,
      language_ids: [],
      cpm_floor_to_confirm: 0,
      ip_range_type: 1,
      use_whitelist_ip: true,
      keywords: [],
      domainsChanged: props.domain.length > 0,
      limits_slow_stop: false,
      limits_rps: false,
      slow_stop_factor: DEFAULT_SLOW_STOP_FACTOR,
      rps_factor: DEFAULT_RPS_FACTOR,
      isp_mode: IspMode.Any,
      retargeting_list_ids: []
    })

    if (props.mediaKitId) {
      mediaKitsStore.FetchItem(props.mediaKitId).then(() => {
        let params: any = {}
        const mediaKitObject = mediaKitsStore.itemById(props.mediaKitId)
        mediaKit.value = mediaKitObject

        params.name = `Buy traffic from ${mediaKitObject.name}`
        params.stream = mediaKitObject.streams[0]
        params.type_id = mediaKitObject.types[0]
        params.domainsChanged = true

        if (mediaKitObject.is_single_website) {
          params.use_whitelist_domains = true
          params.whitelist_domains = [mediaKitObject.domain]
        } else {
          params.ron_type = RonType.Only
          params.spot_ids = setMediaKitSpots(params.type_id, params.stream)
        }
        campaignItem.value = { ...campaignItem.value, ...params }
      })
    }

    const { provideValidation, setValidationErrors } = useValidation(
      CampaignValidationErrorsKey
    )
    const creativesToClone = computed(() => {
      return creativesStore.getClonedItems
    })

    const errors = provideValidation()

    const loading = ref(false)
    const { router } = useRouter()
    const { processOnboarding } = useOnboardingRedirect()

    const onSuccess = async (item?: ICampaign) => {
      event('company', { event_category: 'create' })

      const onboardingFlow = await processOnboarding(false)

      const route =
        item && creativesToClone.value.length
          ? `/campaigns/${item.id}/creatives/${creativesToClone.value[0]}/clone`
          : item
            ? `/campaigns/${item.id}/creatives/new`
            : '/campaigns'
      await router.push(route)

      if (!onboardingFlow)
        ElMessage.success({
          message: 'Campaign has been created',
          type: 'success'
        })
    }
    const setMediaKitSpots = (typeId: number, stream: number) => {
      if (!mediaKit.value) return []

      return mediaKit.value.spots
        .filter((spot: any) => spot.stream === stream && spot.type === typeId)
        .map((spot: any) => spot.id)
    }

    const onSubmit = async (campaign: ICampaignAttributes) => {
      errors.value = {}
      await safeSubmitProcessing(
        loading,
        () => campaignsStore.SaveItem(campaign),
        (item?: ICampaign) => onSuccess(item),
        (err) => {
          event('company', { event_category: 'create' })
          setValidationErrors(err, errors)
        }
      )
    }

    const update = (campaign: ICampaignAttributes) => {
      spotsListsStore.FetchItems({
        start: 0,
        limit: 1000,
        filter: {
          type_id: campaign.type_id,
          stream: campaign.stream,
          ron_type: campaign.ron_type
        }
      })
      if (mediaKit.value && !mediaKit.value.is_single_website) {
        // @ts-ignore: Object is possibly 'undefined'
        campaignItem.value.spot_ids = setMediaKitSpots(
          campaign.type_id,
          campaign.stream
        )
      }
    }

    const loadClonedCampaign = (id: strin) => {
      if (id) {
        loadingCampaign.value = true
        campaignsStore
          .FetchItem(id)
          .then(({ data }) => {
            campaignItem.value = { ...data.attributes, id: undefined }
            loadingCampaign.value = false
          })
          .catch(() => {
            loadingCampaign.value = false
          })
      }
    }

    watch(
      (_: any) => props.id,
      (val: string) => {
        loadClonedCampaign(props.id)
      }
    )
    onMounted(() => {
      loadClonedCampaign(props.id)
    })

    return {
      campaignItem,
      errors,
      loading,
      loadingCampaign,
      onSubmit,
      update,
      mediaKit,
      creativesToClone
    }
  }
})
</script>
